import React from "react";
import GatsbyImage from "gatsby-image";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import { CartItem, Product } from "../types";
import Button from "./Button";
import { convertCentsToCurrency } from "../utils";
import { PRODUCT_IMAGE_ASPECT_RATIO } from "../constants";
import { calculateCartItemId } from "./Layout";

export interface ProductContainerProps extends Product {
  addToCart: (cartItem: CartItem) => void;
  cartItemObject?: { [key: string]: CartItem };
  orderLimit?: number;
}

const ProductContainer: React.FC<ProductContainerProps> = ({
  name,
  description,
  productImage,
  displayPrice,
  id,
  category,
  multiplePriceOptions,
  priceOptionArray,
  addToCart,
  cartItemObject,
  orderLimit,
}) => {
  const [selectedPriceOption, setSelectedPriceOption] = React.useState<
    null | string
  >(null);
  const [isClient, setIsClient] = React.useState(false);
  const defaultPriceOption = priceOptionArray[0];

  const handleClick = () => {
    const price = multiplePriceOptions
      ? priceOptionArray.find(({ key }) => key === selectedPriceOption)?.price
      : defaultPriceOption.price;
    const priceOption = multiplePriceOptions
      ? selectedPriceOption
      : defaultPriceOption.key;

    if (price === undefined || priceOption === null) {
      return;
    }

    const cartItem: CartItem = {
      name,
      productId: id,
      multiplePriceOptions,
      productImage,
      price,
      priceOption,
      quantity: 1,
      category,
    };

    addToCart(cartItem);
    setSelectedPriceOption(null);
  };

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  const quantity = React.useMemo(() => {
    if (!isClient || !cartItemObject) {
      return null;
    }

    return priceOptionArray.reduce((acc, option) => {
      const cartItemId = calculateCartItemId({
        priceOption: option.key,
        productId: id,
      });
      acc += cartItemObject[cartItemId]?.quantity || 0;
      return acc;
    }, 0);
  }, [isClient, cartItemObject, priceOptionArray]);

  return (
    <div className="shadow-md w-full flex flex-col bg-white rounded overflow-hidden justify-between h-full">
      <div>
        <div className="w-full relative">
          {quantity ? (
            <div className="cart-qty-overlay">
              <h5 className="mb-1 text-3xl">{quantity}</h5>
              <p className="text-sm">IN YOUR CART</p>
            </div>
          ) : null}
          <GatsbyImage
            fluid={{
              ...productImage.childImageSharp.fluid,
              aspectRatio: PRODUCT_IMAGE_ASPECT_RATIO,
            }}
          />
        </div>
        <div className="py-1 px-2">
          <h6 className="font-bold text-xl">{name}</h6>
        </div>
        <div className="py-1 px-2">
          <p className="text-gray-600 leading-5 text-sm">{description}</p>
        </div>
      </div>
      {multiplePriceOptions && (
        <PriceOptionSelect
          priceOptionArray={priceOptionArray}
          selectedPriceOption={selectedPriceOption}
          setSelectedPriceOption={setSelectedPriceOption}
        />
      )}
      <div className="p-2">
        {!multiplePriceOptions && (
          <h5 className="font-bold text-xl mb-1">
            ${convertCentsToCurrency(displayPrice)}
          </h5>
        )}
        <Button
          variant="primaryContained"
          size="small"
          additionalClasses="w-full"
          onClick={handleClick}
          disabled={
            (multiplePriceOptions && !selectedPriceOption) ||
            Boolean(quantity && orderLimit && quantity >= orderLimit)
          }
        >
          ADD TO CART
        </Button>
      </div>
    </div>
  );
};

function PriceOptionSelect({
  priceOptionArray,
  setSelectedPriceOption,
  selectedPriceOption,
}: {
  priceOptionArray: { price: number; key: string }[];
  selectedPriceOption: string | null;
  setSelectedPriceOption: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  return (
    <div className="px-2">
      <TextField
        label="Select an option"
        variant="outlined"
        fullWidth
        margin="normal"
        select
        size="small"
        onChange={e => setSelectedPriceOption(e.target.value)}
        value={selectedPriceOption}
      >
        {priceOptionArray.map(({ key, price }) => {
          return (
            <MenuItem key={key} value={key}>
              {key} - ${convertCentsToCurrency(price)}
            </MenuItem>
          );
        })}
      </TextField>
    </div>
  );
}

export default ProductContainer;
